import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

interface Question {
  id: number;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
}

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const ExamPage: React.FC = () => {
  const [hallTicket, setHallTicket] = useState('');
  const [hallVerified, setHallVerified] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [loading, setLoading] = useState(false);
  const [totalTimeLeft, setTotalTimeLeft] = useState(900); // 15 min
  const [questionTimeLeft, setQuestionTimeLeft] = useState(60); // 60 sec per question

  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);

  const handleHallTicketVerify = async () => {
    if (!hallTicket) {
      MySwal.fire('Required', 'Please enter your hall ticket number', 'warning');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/exam/verify-hall-ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ hall_ticket_no: hallTicket }),
      });

      const data = await res.json();
      if (!data.success) {
        MySwal.fire('Error', data.message || 'Verification failed', 'error');
        setLoading(false);
        return;
      }

      const contestEndTime = new Date(data.contest_end_time);
      if (new Date() > contestEndTime) {
        MySwal.fire('Time Up', 'The contest has already ended. You cannot start the exam now.', 'error');
        setLoading(false);
        return;
      }

      await fetchQuestions();
      setHallVerified(true);
    } catch (err) {
      MySwal.fire('Error', 'Error verifying hall ticket', 'error');
    }
    setLoading(false);
  };

  const fetchQuestions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/exam/questions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setQuestions(data.questions);
      } else {
        MySwal.fire('Error', 'Unable to load questions.', 'error');
      }
    } catch (err) {
      MySwal.fire('Error', 'Error loading questions', 'error');
    }
  };

  const handleOptionSelect = (option: string) => {
    setAnswers((prev) => ({ ...prev, [questions[currentIndex].id]: option }));
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      handleSubmit(); // Auto submit if last question
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('authToken');
      await fetch(`${API_BASE_URL}/exam/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ answers }),
      });

      await fetch(`${API_BASE_URL}/exam/mark-attempted`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      navigate('/exam/submitted');
    } catch (err) {
      MySwal.fire('Error', 'Error submitting exam', 'error');
    }
  };

  // ⏰ Total timer effect
  useEffect(() => {
    if (!hallVerified) return;
    const totalTimer = setInterval(() => {
      setTotalTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(totalTimer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(totalTimer);
  }, [hallVerified]);

  // ⏱ Per-question timer effect
  useEffect(() => {
    if (!hallVerified) return;
    setQuestionTimeLeft(60);
    const questionTimer = setInterval(() => {
      setQuestionTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(questionTimer);
          handleNext();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(questionTimer);
  }, [currentIndex, hallVerified]);

  // 🔒 Prevent back navigation
  useEffect(() => {
    if (!hallVerified) return;
    window.history.pushState(null, '', window.location.href);
    const handleBack = (e: PopStateEvent) => {
      window.history.pushState(null, '', window.location.href);
      MySwal.fire('Blocked', 'You cannot go back during the exam.', 'warning');
    };
    window.addEventListener('popstate', handleBack);
    return () => window.removeEventListener('popstate', handleBack);
  }, [hallVerified]);

  // 🚨 Mark attempted on tab close
  useEffect(() => {
    if (!hallVerified) return;
    const token = localStorage.getItem('authToken');

    const handleUnload = (e: BeforeUnloadEvent) => {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const url = `${API_BASE_URL}/exam/mark-attempted?token=${encodeURIComponent(token)}`;
      navigator.sendBeacon(url, JSON.stringify({}));

      e.preventDefault();
      e.returnValue = '';
    };


    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [hallVerified]);

  // --- UI ---
  if (!hallVerified) {
    return (
      <div className="max-w-xl mx-auto mt-20 bg-white shadow p-6 rounded">
        <h2 className="text-2xl font-bold mb-4">📝 Exam Rules</h2>
        <ul className="list-disc ml-5 text-sm mb-6 text-gray-700">
          <li>Total time will be shown at the top.</li>
          <li>Each question has a dedicated timer.</li>
          <li>+2 for each correct answer, -1 for each incorrect answer.</li>
          <li>Do not refresh or close the browser tab during the exam.</li>
          <li>Answers cannot be changed once submitted.</li>
        </ul>
        <label className="block mb-2 font-semibold text-sm">Enter your Hall Ticket Number:</label>
        <input
          value={hallTicket}
          onChange={(e) => setHallTicket(e.target.value)}
          className="w-full px-4 py-2 border rounded mb-4"
          placeholder="e.g., QZ2025A1234"
        />
        <button
          onClick={handleHallTicketVerify}
          disabled={loading}
          className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 shadow"
        >
          {loading ? 'Verifying...' : 'Start Exam'}
        </button>
      </div>
    );
  }

  if (questions.length === 0) return <div className="text-center mt-10">No questions available</div>;

  const q = questions[currentIndex];
  const selected = answers[q.id];

  return (
    <div className="max-w-2xl mx-auto mt-20 bg-white shadow p-6 rounded">
      <div className="flex justify-between text-sm text-gray-600 mb-2">
        <div>⏱ Total Time Left: <span className="text-red-600 font-semibold">{formatTime(totalTimeLeft)}</span></div>
        <div>🕐 Per Question: <span className="text-blue-600 font-semibold">{formatTime(questionTimeLeft)}</span></div>
      </div>
      <h2 className="text-xl font-semibold mb-4">Question {currentIndex + 1} of {questions.length}</h2>
      <p className="text-lg font-medium mb-6">{q.question_text}</p>
      {(['a', 'b', 'c', 'd'] as const).map((opt) => (
        <button
          key={opt}
          onClick={() => handleOptionSelect(opt)}
          className={`block w-full text-left px-4 py-2 rounded border mb-2 transition
            ${selected === opt ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
        >
          {opt.toUpperCase()}. {q[`option_${opt}` as keyof Question]}
        </button>
      ))}
      <div className="mt-6 flex justify-between">
        <button
          onClick={handleNext}
          disabled={currentIndex === questions.length - 1}
          className="bg-blue-500 text-white px-6 py-2 rounded disabled:bg-gray-300"
        >
          Next
        </button>
        {currentIndex === questions.length - 1 && (
          <button
            onClick={handleSubmit}
            className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
          >
            Submit Exam
          </button>
        )}
      </div>
    </div>
  );
};

export default ExamPage;
